import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { AudioRecorderCore, type AudioRecorderStatus } from './AudioRecorderCore';

interface Props {
  placeholder?: string;
  label?: string;
  inputTextValue: string;
  audioBlob: Blob | null;
  onTextChange: (value: string) => void;
  onAudioChange: (blob: Blob | null, transcription?: string) => void;
  isSubmitting?: boolean;
  minCharacters?: number;
}

const TEXTAREA_MIN_HEIGHT_PX = 128;
const TEXTAREA_MAX_HEIGHT_PX = 540;

export const TextOrAudioResponseQuestion: React.FC<Props> = ({
  placeholder,
  label,
  inputTextValue,
  audioBlob,
  onTextChange,
  onAudioChange,
  isSubmitting,
  minCharacters,
}) => {
  const [, setAudioStatus] = useState<AudioRecorderStatus>(audioBlob ? 'ready' : 'idle');
  const [error, setError] = useState<string | null>(null);
  const [transcribedText, setTranscribedText] = useState<string | null>(null);
  const textBeforeTranscriptRef = useRef('');
  // The recorder keeps the completion callback from when recording stopped; read the latest text.
  const latestTextRef = useRef(inputTextValue);
  latestTextRef.current = inputTextValue;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const textareaId = `text-response-${useId().replace(/:/g, '')}`;
  const accessibleLabel = label?.trim() || placeholder?.trim() || 'Response';

  const resizeTextarea = useCallback(() => {
    const element = textareaRef.current;
    if (!element) {
      return;
    }

    element.style.height = 'auto';
    const nextHeight = Math.min(
      Math.max(element.scrollHeight, TEXTAREA_MIN_HEIGHT_PX),
      TEXTAREA_MAX_HEIGHT_PX,
    );
    element.style.height = `${nextHeight}px`;
    element.style.overflowY = element.scrollHeight > TEXTAREA_MAX_HEIGHT_PX ? 'auto' : 'hidden';
  }, []);

  useLayoutEffect(() => {
    resizeTextarea();
  }, [inputTextValue, resizeTextarea]);

  useEffect(() => {
    if (!audioBlob) {
      setAudioStatus('idle');
      setTranscribedText(null);
    }
  }, [audioBlob]);

  useEffect(() => {
    if (transcribedText !== null && inputTextValue !== transcribedText) {
      setTranscribedText(null);
    }
  }, [inputTextValue, transcribedText]);

  const handleTextInputChange = (value: string) => {
    setError(null);
    onTextChange(value);
  };

  const handleRecordingComplete = (blob: Blob | null, transcription?: string) => {
    setError(null);

    if (blob === null) {
      const shouldClearText =
        transcribedText !== null &&
        latestTextRef.current.trim().length > 0 &&
        latestTextRef.current === transcribedText;

      onAudioChange(null);
      setTranscribedText(null);

      if (shouldClearText) {
        // Removing the recording drops only the dictated text, not what was typed before it.
        onTextChange(textBeforeTranscriptRef.current);
      }
    } else if (transcription) {
      // Dictation adds to the answer instead of replacing typed text.
      const currentText = latestTextRef.current;
      const combined = currentText
        ? `${currentText}${/\s$/.test(currentText) ? '' : ' '}${transcription}`
        : transcription;
      textBeforeTranscriptRef.current = currentText;
      onAudioChange(blob, combined);
      setTranscribedText(combined);
      onTextChange(combined);
    } else {
      onAudioChange(blob, transcription);
    }
  };

  const handleError = (errorMessage: string) => {
    console.error('[TextOrAudioResponseQuestion] Error:', errorMessage);
    setError(errorMessage);
    onAudioChange(null);
  };

  return (
    <div className='w-full'>
      <div className="flex flex-col items-start gap-2 self-stretch">
        <div className="relative w-full">
          <label htmlFor={textareaId} className="sr-only">
            {accessibleLabel}
          </label>
          <textarea
            ref={textareaRef}
            className="relative block w-full min-h-[128px] max-h-[700px] resize-none overflow-y-auto border-0 bg-transparent px-0 pb-2 pr-14 pt-0 text-base text-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-xs"
            style={{ height: `${TEXTAREA_MIN_HEIGHT_PX}px` }}
            onChange={(event) => handleTextInputChange(event.target.value)}
            onInput={resizeTextarea}
            value={inputTextValue}
            disabled={isSubmitting}
            placeholder={placeholder || 'Enter your response'}
            id={textareaId}
          />
          <div className="pointer-events-none absolute bottom-0 right-0 flex items-end">
            <fieldset disabled={isSubmitting} className="pointer-events-auto m-0 border-0 p-0">
            <AudioRecorderCore
              onRecordingComplete={handleRecordingComplete}
              onError={handleError}
              audioBlob={audioBlob}
              onStatusChange={setAudioStatus}
            />
            </fieldset>
          </div>
        </div>

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        {minCharacters && (
          <div className="relative w-fit text-xs font-[number:var(--body-s-font-weight)] text-foundation-base-darkbase-dark-500 tracking-[var(--body-s-letter-spacing)] leading-[var(--body-s-line-height)] whitespace-nowrap [font-style:var(--body-s-font-style)]">
            Write minimum {minCharacters} characters
          </div>
        )}
      </div>
    </div>
  );
};
